import type {
  MasterOp,
  StockCommandMasterEffectV1,
  StockCommandV1,
  SyncEventInput,
} from '@trackeat/shared';
import { openLocalDb, flushLocalDb, REGISTER_OWNER_FIELD } from '../db/local.js';
import { isOfflineRegisterTable, validateOfflineRegisterPatch } from '@trackeat/shared';
import { deepEqualJson } from '@trackeat/shared/sync';
import { confirmedEventLocalIdentity } from './eventsLocalKey.js';
import type { PulledMasterChange } from './pulledMaster.js';
import {
  acknowledgeStockCommand,
  type StockCommandAckResult,
} from './stockCommandOutbox.js';
import type {
  AtomicStockCommandStorage,
  EnqueuedStockCommandHandle,
  LinkedOptimisticStockEvent,
  StockCommandFinalization,
  StockCommandOutboxRecord,
  StockCommandStorageTransaction,
} from './stockCommandOutbox.js';

type SqlTask = {
  statement: string;
  values?: unknown[];
};

interface StockCommandWritebackPlan {
  tasks: SqlTask[];
  authoritativeMasters: Map<string, OptimisticMasterState>;
}

interface PendingCommandRow {
  command_id: string;
  tenant_id: string;
  digest: string;
  command_json: string;
  master_effects_json: string;
  status: StockCommandOutboxRecord<StockCommandV1>['status'];
  attempts: number;
  optimistic_event_ids: string;
  last_error: string | null;
  conflict_json: string | null;
  queued_at: string;
  updated_at: string;
}

interface FinalizationRow {
  command_id: string;
  digest: string;
  outcome: StockCommandFinalization['outcome'];
  reason: string | null;
  finalized_at: string;
}

interface LinkedEventRow {
  command_id: string;
  event_id: string;
  event_json: string;
}

export interface OptimisticMasterState {
  existed: boolean;
  tenant_id: string | null;
  version: number | null;
  row_json: string | null;
  archived: number | null;
  updated_at: string | null;
}

export interface OptimisticMasterLayer {
  command_id: string;
  effect: StockCommandMasterEffectV1;
}

interface PendingMasterLayerRow {
  command_id: string;
  table_name: string;
  id: string;
  existed: number;
  tenant_id: string | null;
  version: number | null;
  row_json: string | null;
  archived: number | null;
  updated_at: string | null;
  master_effects_json: string;
  queued_at?: string;
}

interface PendingAtomicMasterLayerRow {
  request_id: string;
  table_name: string;
  id: string;
  existed: number;
  tenant_id: string | null;
  version: number | null;
  row_json: string | null;
  archived: number | null;
  updated_at: string | null;
  effect_json: string;
  queued_at: string;
}

function serviceDateInRome(isoTimestamp: unknown): string | null {
  if (typeof isoTimestamp !== 'string') return null;
  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const byType = new Map(parts.map((part) => [part.type, part.value]));
  const year = byType.get('year');
  const month = byType.get('month');
  const day = byType.get('day');
  return year && month && day ? `${year}-${month}-${day}` : null;
}

interface UnifiedOptimisticMasterLayer {
  ownerKind: 'atomic' | 'stock';
  ownerId: string;
  queuedAt: string;
  snapshot: OptimisticMasterState;
  effect: StockCommandMasterEffectV1;
}

export interface ReplayedMasterLayer {
  command_id: string;
  snapshot: OptimisticMasterState;
}

export interface ReplayedMasterLayers {
  finalState: OptimisticMasterState;
  layers: ReplayedMasterLayer[];
}

export interface PendingStockCommandBatch {
  record: StockCommandOutboxRecord<StockCommandV1>;
  handle: EnqueuedStockCommandHandle;
  events: SyncEventInput[];
}

function parseCommandRow(row: PendingCommandRow): StockCommandOutboxRecord<StockCommandV1> {
  return {
    command_id: row.command_id,
    digest: row.digest,
    command: JSON.parse(row.command_json) as StockCommandV1,
    master_effects: JSON.parse(
      row.master_effects_json,
    ) as StockCommandMasterEffectV1[],
    status: row.status,
    attempts: Number(row.attempts),
    optimistic_event_ids: JSON.parse(row.optimistic_event_ids) as string[],
    last_error: row.last_error,
    conflict: row.conflict_json
      ? JSON.parse(row.conflict_json) as StockCommandOutboxRecord['conflict']
      : null,
    queued_at: row.queued_at,
    updated_at: row.updated_at,
  };
}

function parseFinalizationRow(row: FinalizationRow): StockCommandFinalization {
  return {
    command_id: row.command_id,
    digest: row.digest,
    outcome: row.outcome,
    reason: row.reason,
    finalized_at: row.finalized_at,
  };
}

function commandValues(record: StockCommandOutboxRecord<StockCommandV1>): unknown[] {
  return [
    record.command_id,
    record.command.tenant_id,
    record.digest,
    JSON.stringify(record.command),
    JSON.stringify(record.master_effects),
    record.status,
    record.attempts,
    JSON.stringify(record.optimistic_event_ids),
    record.last_error,
    record.conflict ? JSON.stringify(record.conflict) : null,
    record.queued_at,
    record.updated_at,
  ];
}

function collectReferencedIds(value: unknown, output: Set<string>): void {
  if (typeof value === 'string') {
    if (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        .test(value)
    ) {
      output.add(value);
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectReferencedIds(item, output);
    return;
  }
  if (!value || typeof value !== 'object') return;
  for (const nested of Object.values(value as Record<string, unknown>)) {
    collectReferencedIds(nested, output);
  }
}

function masterStateFromSnapshot(
  snapshot: Pick<
    PendingMasterLayerRow,
    'existed' | 'tenant_id' | 'version' | 'row_json' | 'archived' | 'updated_at'
  >,
): OptimisticMasterState {
  return {
    existed: Number(snapshot.existed) === 1,
    tenant_id: snapshot.tenant_id,
    version: snapshot.version == null ? null : Number(snapshot.version),
    row_json: snapshot.row_json,
    archived: snapshot.archived == null ? null : Number(snapshot.archived),
    updated_at: snapshot.updated_at,
  };
}

function applyMasterEffectForReplay(
  state: OptimisticMasterState,
  effect: StockCommandMasterEffectV1,
  updatedAt: string,
): OptimisticMasterState {
  const previousRow = state.existed && state.row_json
    ? JSON.parse(state.row_json) as Record<string, unknown>
    : {};
  const nextRow: Record<string, unknown> = {
    ...previousRow,
    ...effect.patch,
    id: effect.id,
  };
  if (effect.op === 'archive') {
    nextRow.archived_at =
      (effect.patch.archived_at as string | undefined)
      ?? updatedAt;
  }
  const nextVersion = effect.op === 'insert'
    ? 0
    : state.existed
      ? Number(state.version ?? effect.base_version) + 1
      : effect.base_version + 1;
  nextRow.version = nextVersion;
  const tenantId = (
    nextRow.tenant_id as string | null | undefined
  ) ?? state.tenant_id ?? null;
  return {
    existed: true,
    tenant_id: tenantId,
    version: nextVersion,
    row_json: JSON.stringify(nextRow),
    archived: nextRow.archived_at ? 1 : 0,
    updated_at: updatedAt,
  };
}

/**
 * Rebuild one optimistic master stack from a known base. Each surviving
 * command receives a new snapshot of the state immediately before its effect.
 * That makes later rejection safe even when an earlier overlapping command was
 * removed (save→save, print→cancel, or ACK writeback followed by a pending edit).
 */
export function replayOptimisticMasterLayersForTest(
  base: OptimisticMasterState,
  layers: readonly OptimisticMasterLayer[],
  updatedAt: string,
): ReplayedMasterLayers {
  let state = { ...base };
  const replayed: ReplayedMasterLayer[] = [];
  for (const layer of layers) {
    replayed.push({
      command_id: layer.command_id,
      snapshot: { ...state },
    });
    state = applyMasterEffectForReplay(state, layer.effect, updatedAt);
  }
  return { finalState: state, layers: replayed };
}

function effectForLayerTarget(
  row: PendingMasterLayerRow,
): StockCommandMasterEffectV1 {
  const effects = JSON.parse(
    row.master_effects_json,
  ) as StockCommandMasterEffectV1[];
  const effect = effects.find((candidate) => (
    candidate.table === row.table_name && candidate.id === row.id
  ));
  if (!effect) {
    throw new Error(
      `stock_command_master_effect_missing:${row.command_id}:${row.table_name}:${row.id}`,
    );
  }
  return effect;
}

function snapshotUpdateTask(
  commandId: string,
  table: string,
  id: string,
  state: OptimisticMasterState,
): SqlTask {
  return {
    statement:
      `UPDATE pending_stock_command_master_snapshots
          SET existed = ?, tenant_id = ?, version = ?, row_json = ?,
              archived = ?, updated_at = ?
        WHERE command_id = ?
          AND table_name = ?
          AND id = ?`,
    values: [
      state.existed ? 1 : 0,
      state.tenant_id,
      state.version,
      state.row_json,
      state.archived,
      state.updated_at,
      commandId,
      table,
      id,
    ],
  };
}

function atomicSnapshotUpdateTask(
  requestId: string,
  table: string,
  id: string,
  state: OptimisticMasterState,
): SqlTask {
  return {
    statement:
      `UPDATE pending_atomic_rpc_master_snapshots_v1
          SET existed = ?, tenant_id = ?, version = ?, row_json = ?,
              archived = ?, updated_at = ?
        WHERE request_id = ?
          AND table_name = ?
          AND id = ?`,
    values: [
      state.existed ? 1 : 0,
      state.tenant_id,
      state.version,
      state.row_json,
      state.archived,
      state.updated_at,
      requestId,
      table,
      id,
    ],
  };
}

// A remote command receipt/pull may predate local paper completion. Never
// regress a terminal print projection; its outcome outbox owns delivery.
function masterStateWriteTasks(
  table: string,
  id: string,
  state: OptimisticMasterState,
): SqlTask[] {
  if (!state.existed) {
    return [{
      statement:
        `DELETE FROM masters_local
          WHERE table_name = ?
            AND id = ?`,
      values: [table, id],
    }];
  }
  return [{
    statement:
      `INSERT INTO masters_local(
         table_name, id, tenant_id, version, row, archived, updated_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(table_name,id) DO UPDATE SET
         tenant_id = excluded.tenant_id,
         version = excluded.version,
         row = excluded.row,
         archived = excluded.archived,
         updated_at = excluded.updated_at
       WHERE masters_local.table_name <> 'production_label_print_intents_v1'
          OR json_extract(masters_local.row, '$.state') NOT IN ('printed', 'voided')
          OR json_extract(excluded.row, '$.state') IN ('printed', 'voided')`,
    values: [
      table,
      id,
      state.tenant_id,
      Number(state.version ?? 0),
      state.row_json ?? '{}',
      Number(state.archived ?? 0),
      state.updated_at ?? new Date().toISOString(),
    ],
  }];
}

function masterTargetKey(table: string, id: string): string {
  return `${table}:${id}`;
}

function masterWritebackTask(
  table: string,
  row: Record<string, unknown>,
  fallbackVersion = 0,
): { task: SqlTask; state: OptimisticMasterState } {
  if (typeof row.id !== 'string') {
    throw new Error('stock_command_writeback_master_id_missing');
  }
  const version = Number(row.version ?? fallbackVersion);
  if (!Number.isInteger(version) || version < 0) {
    throw new Error('stock_command_writeback_master_version_invalid');
  }
  const updatedAt = typeof row.updated_at === 'string'
    ? row.updated_at
    : new Date().toISOString();
  const tenantId = (row.tenant_id as string | null | undefined) ?? null;
  const archived = row.archived_at ? 1 : 0;
  const rowJson = JSON.stringify(row);
  return {
    task: {
      statement:
        `INSERT INTO masters_local(
           table_name, id, tenant_id, version, row, archived, updated_at
         ) VALUES (?, ?, ?, ?, ?, ?, ?)
         ON CONFLICT(table_name,id) DO UPDATE SET
           tenant_id = excluded.tenant_id,
           version = excluded.version,
           row = excluded.row,
           archived = excluded.archived,
           updated_at = excluded.updated_at
         WHERE masters_local.table_name <> 'production_label_print_intents_v1'
            OR json_extract(masters_local.row, '$.state') NOT IN ('printed', 'voided')
            OR json_extract(excluded.row, '$.state') IN ('printed', 'voided')`,
      values: [
        table,
        row.id,
        tenantId,
        version,
        rowJson,
        archived,
        updatedAt,
      ],
    },
    state: {
      existed: true,
      tenant_id: tenantId,
      version,
      row_json: rowJson,
      archived,
      updated_at: updatedAt,
    },
  };
}

function stockCommandWritebackPlan(
  result: Record<string, unknown>,
): StockCommandWritebackPlan {
  const writeback = result.writeback;
  if (!writeback || typeof writeback !== 'object' || Array.isArray(writeback)) {
    throw new Error('stock_command_writeback_missing');
  }
  const raw = writeback as {
    events?: unknown;
    audit_events?: unknown;
    foods?: unknown;
    shipments?: unknown;
    masters?: unknown;
  };
  const auditEvents = raw.audit_events ?? [];
  const shipments = raw.shipments ?? [];
  const masters = raw.masters ?? [];
  if (
    !Array.isArray(raw.events)
    || !Array.isArray(auditEvents)
    || !Array.isArray(raw.foods)
    || !Array.isArray(shipments)
    || !Array.isArray(masters)
  ) {
    throw new Error('stock_command_writeback_invalid');
  }

  const tasks: SqlTask[] = [];
  const authoritativeMasters = new Map<string, OptimisticMasterState>();
  for (const value of [...raw.events, ...auditEvents]) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('stock_command_writeback_event_invalid');
    }
    const event = value as Record<string, unknown>;
    const key = confirmedEventLocalIdentity(event);
    tasks.push({
      statement:
        `INSERT OR REPLACE INTO events_local(
           id, server_id, tenant_id, kind, business_id, client_ts, server_ts, rev,
           payload, self_hash, prev_hash
         ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      values: [
        key.id,
        key.serverId,
        event.tenant_id as string,
        event.kind as string,
        (event.business_id as string | null) ?? null,
        event.client_ts as string,
        (event.server_ts as string | null) ?? null,
        (event.server_rev as number | null) ?? null,
        JSON.stringify(event.payload ?? {}),
        (event.self_hash as string | null) ?? null,
        (event.prev_hash as string | null) ?? null,
      ],
    });
  }
  for (const value of raw.foods) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('stock_command_writeback_food_invalid');
    }
    const food = value as Record<string, unknown>;
    if (typeof food.id !== 'string') {
      throw new Error('stock_command_writeback_food_id_missing');
    }
    const write = masterWritebackTask('foods', food);
    tasks.push(write.task);
    authoritativeMasters.set(masterTargetKey('foods', food.id), write.state);
  }
  for (const value of shipments) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('stock_command_writeback_shipment_invalid');
    }
    const shipment = value as Record<string, unknown>;
    if (typeof shipment.id !== 'string') {
      throw new Error('stock_command_writeback_shipment_id_missing');
    }
    const write = masterWritebackTask('shipments', shipment);
    tasks.push(write.task);
    authoritativeMasters.set(
      masterTargetKey('shipments', shipment.id),
      write.state,
    );
  }
  for (const value of masters) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('stock_command_writeback_master_invalid');
    }
    const master = value as Record<string, unknown>;
    const table = master.table;
    const row = master.row;
    if (
      typeof table !== 'string'
      || !row
      || typeof row !== 'object'
      || Array.isArray(row)
    ) {
      throw new Error('stock_command_writeback_master_invalid');
    }
    const typedRow = row as Record<string, unknown>;
    const write = masterWritebackTask(
      table,
      typedRow,
      Number(master.version ?? 0),
    );
    tasks.push(write.task);
    authoritativeMasters.set(
      masterTargetKey(table, typedRow.id as string),
      write.state,
    );
  }
  return { tasks, authoritativeMasters };
}

let storageQueue: Promise<unknown> = Promise.resolve();

function serializeStorage<Result>(work: () => Promise<Result>): Promise<Result> {
  const run = storageQueue.then(work, work);
  storageQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Shared serialization boundary for every durable atomic writer that overlays
 * `masters_local`. Production-plan/day RPC outboxes use this instead of a
 * second mutex, so enqueue, pull replay, StockCommand ACK and rollback cannot
 * observe one another halfway through a local transaction.
 */
export function withMasterCacheSerialization<Result>(
  work: () => Promise<Result>,
): Promise<Result> {
  return serializeStorage(work);
}

/**
 * SQLite adapter for the pure stock-command lifecycle.
 *
 * The lifecycle callback works against a staged operation list; all staged
 * writes are committed with one native executeTransaction call. A module-level
 * queue prevents two callbacks from reading the same pre-transaction snapshot.
 */
function runStorageTransaction<Result>(
  work: (tx: StockCommandStorageTransaction<StockCommandV1>) => Promise<Result>,
  writebackPlan: StockCommandWritebackPlan | null = null,
): Promise<Result> {
  return serializeStorage(async () => {
      const db = await openLocalDb(null);
      const tasks: SqlTask[] = [];
      const commandOverlay = new Map<string, StockCommandOutboxRecord<StockCommandV1> | null>();
      const finalizationOverlay = new Map<string, StockCommandFinalization>();
      const linkedEventOverlay = new Map<string, Set<string>>();
      let writebackScheduled = false;

      const getCommand = async (
        commandId: string,
      ): Promise<StockCommandOutboxRecord<StockCommandV1> | null> => {
        if (commandOverlay.has(commandId)) return commandOverlay.get(commandId) ?? null;
        const result = await db.query(
          `SELECT command_id, tenant_id, digest, command_json,
                  master_effects_json, status, attempts,
                  optimistic_event_ids, last_error, conflict_json, queued_at, updated_at
             FROM pending_stock_commands
            WHERE command_id = ?`,
          [commandId],
        );
        const row = result.values?.[0] as PendingCommandRow | undefined;
        const parsed = row ? parseCommandRow(row) : null;
        commandOverlay.set(commandId, parsed);
        return parsed;
      };

      const getFinalization = async (
        commandId: string,
      ): Promise<StockCommandFinalization | null> => {
        const staged = finalizationOverlay.get(commandId);
        if (staged) return staged;
        const result = await db.query(
          `SELECT command_id, digest, outcome, reason, finalized_at
             FROM stock_command_finalizations
            WHERE command_id = ?`,
          [commandId],
        );
        const row = result.values?.[0] as FinalizationRow | undefined;
        return row ? parseFinalizationRow(row) : null;
      };

      const linkedIds = async (commandId: string): Promise<Set<string>> => {
        const staged = linkedEventOverlay.get(commandId);
        if (staged) return staged;
        const result = await db.query(
          `SELECT event_id
             FROM pending_stock_command_events
            WHERE command_id = ?`,
          [commandId],
        );
        const ids = new Set((result.values ?? []).map((value) => (
          (value as { event_id: string }).event_id
        )));
        linkedEventOverlay.set(commandId, ids);
        return ids;
      };

      const tx: StockCommandStorageTransaction<StockCommandV1> = {
        getCommand,
        getFinalization,
        async addCommand(record) {
          commandOverlay.set(record.command_id, record);
          tasks.push({
            statement:
              `INSERT INTO pending_stock_commands(
                 command_id, tenant_id, digest, command_json,
                 master_effects_json, status, attempts,
                 optimistic_event_ids, last_error, conflict_json, queued_at, updated_at
               ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            values: commandValues(record),
          });
          const referencedIds = new Set<string>();
          collectReferencedIds(record.command, referencedIds);
          const commandScope =
            record.command as unknown as Record<string, unknown>;
          const commandBusinessId =
            typeof commandScope.business_id === 'string'
              ? commandScope.business_id
              : null;
          const commandReason =
            typeof commandScope.reason === 'string'
              ? commandScope.reason
              : '';
          const productionIds = new Set<string>();
          const productionServiceDates = new Set<string>();
          if (
            record.command.movement_class === 'production'
            && typeof record.command.production_id === 'string'
          ) {
            productionIds.add(record.command.production_id);
          }
          for (const effect of record.master_effects) {
            referencedIds.add(effect.id);
            collectReferencedIds(effect.patch, referencedIds);
            if (effect.table === 'productions') {
              productionIds.add(effect.id);
              const effectDate = serviceDateInRome(effect.patch.produced_at);
              if (effectDate) productionServiceDates.add(effectDate);
            }
          }
          for (const productionId of productionIds) {
            const productionResult = await db.query(
              `SELECT row
                 FROM masters_local
                WHERE table_name = 'productions'
                  AND id = ?
                LIMIT 1`,
              [productionId],
            );
            const rowJson = (
              productionResult.values?.[0] as { row?: string } | undefined
            )?.row;
            if (!rowJson) continue;
            try {
              const row = JSON.parse(rowJson) as {
                produced_at?: unknown;
                business_id?: unknown;
              };
              if (
                row.business_id === commandBusinessId
                || row.business_id == null
              ) {
                const rowDate = serviceDateInRome(row.produced_at);
                if (rowDate) productionServiceDates.add(rowDate);
              }
            } catch {
              throw new Error(
                `stock_command_production_scope_corrupt:${productionId}`,
              );
            }
          }
          if (record.command.movement_class === 'production') {
            for (
              const dateToken
              of commandReason.match(/\d{4}-\d{2}-\d{2}/g) ?? []
            ) {
              productionServiceDates.add(dateToken);
            }
          }
          if (referencedIds.size > 0) {
            const atomicResult = await db.query(
              `SELECT DISTINCT rpc.request_id,rpc.tenant_id,rpc.business_id,
                      rpc.kind,rpc.request_json,rpc.status,rpc.queued_at,
                      snapshot.id
                 FROM pending_atomic_rpcs_v1 AS rpc
                 JOIN pending_atomic_rpc_master_snapshots_v1 AS snapshot
                   ON snapshot.request_id = rpc.request_id
                WHERE rpc.tenant_id = ?
                ORDER BY rpc.queued_at,rpc.request_id`,
              [record.command.tenant_id],
            );
            const dependencyOwners = new Map<string, {
              request_id: string;
              tenant_id: string;
              business_id: string;
              status: string;
              queued_at: string;
            }>();
            for (const value of atomicResult.values ?? []) {
              const row = value as {
                request_id: string;
                tenant_id: string;
                business_id: string;
                kind: string;
                request_json: string;
                status: string;
                queued_at: string;
                id: string;
              };
              let semanticReopenDependency = false;
              if (
                row.kind === 'production_day_transition_v1'
                && row.business_id === commandBusinessId
                && productionServiceDates.size > 0
              ) {
                try {
                  const request = JSON.parse(row.request_json) as {
                    transition?: unknown;
                    sheet_date?: unknown;
                  };
                  semanticReopenDependency =
                    request.transition === 'reopen'
                    && typeof request.sheet_date === 'string'
                    && productionServiceDates.has(request.sheet_date);
                } catch {
                  throw new Error(
                    `stock_command_atomic_dependency_corrupt:${row.request_id}`,
                  );
                }
              }
              if (
                !referencedIds.has(row.id)
                && !semanticReopenDependency
              ) continue;
              if (row.status === 'conflict') {
                throw new Error(
                  `stock_command_atomic_dependency_conflict:${row.request_id}`,
                );
              }
              dependencyOwners.set(row.request_id, row);
            }
            for (const dependency of dependencyOwners.values()) {
              tasks.push({
                statement:
                  `INSERT INTO atomic_rpc_dependents_v1(
                     request_id,dependent_kind,dependent_id,tenant_id,
                     business_id,status,reason,queued_at,updated_at
                   ) VALUES(?,'stock_command',?,?,?,'pending',NULL,?,?)`,
                values: [
                  dependency.request_id,
                  record.command_id,
                  dependency.tenant_id,
                  dependency.business_id,
                  record.queued_at,
                  record.updated_at,
                ],
              });
            }
          }
        },
        async putCommand(record) {
          commandOverlay.set(record.command_id, record);
          tasks.push({
            statement:
              `UPDATE pending_stock_commands
                  SET tenant_id = ?, digest = ?, command_json = ?,
                      master_effects_json = ?, status = ?, attempts = ?,
                      optimistic_event_ids = ?, last_error = ?,
                      conflict_json = ?, queued_at = ?, updated_at = ?
                WHERE command_id = ?`,
            values: [
              record.command.tenant_id,
              record.digest,
              JSON.stringify(record.command),
              JSON.stringify(record.master_effects),
              record.status,
              record.attempts,
              JSON.stringify(record.optimistic_event_ids),
              record.last_error,
              record.conflict ? JSON.stringify(record.conflict) : null,
              record.queued_at,
              record.updated_at,
              record.command_id,
            ],
          });
        },
        async deleteCommand(commandId) {
          commandOverlay.set(commandId, null);
          linkedEventOverlay.delete(commandId);
          tasks.push(
            {
              statement: 'DELETE FROM pending_stock_command_events WHERE command_id = ?',
              values: [commandId],
            },
            {
              statement: 'DELETE FROM pending_stock_commands WHERE command_id = ?',
              values: [commandId],
            },
          );
        },
        async addFinalization(finalization) {
          finalizationOverlay.set(finalization.command_id, finalization);
          tasks.push({
            statement:
              `INSERT INTO stock_command_finalizations(
                 command_id, digest, outcome, reason, finalized_at
               ) VALUES (?, ?, ?, ?, ?)`,
            values: [
              finalization.command_id,
              finalization.digest,
              finalization.outcome,
              finalization.reason,
              finalization.finalized_at,
            ],
          });
        },
        async archiveRejectedCommand(record, reason, rejectedAt) {
          const eventRows = await db.query(
            `SELECT event_json
               FROM pending_stock_command_events
              WHERE command_id = ?
              ORDER BY event_id`,
            [record.command_id],
          );
          const events = (eventRows.values ?? []).map((value) => (
            JSON.parse((value as { event_json: string }).event_json) as SyncEventInput
          ));
          tasks.push({
            statement:
              `INSERT INTO stock_command_reconciliations_v1(
                 command_id,digest,tenant_id,command_json,events_json,
                 master_effects_json,reason,status,resolution,created_at,
                 updated_at,resolved_at
               ) VALUES(?,?,?,?,?,?,?,'reconciliation_required',NULL,?,?,NULL)
               ON CONFLICT(command_id) DO UPDATE SET
                 reason = excluded.reason,
                 updated_at = excluded.updated_at`,
            values: [
              record.command_id,
              record.digest,
              record.command.tenant_id,
              JSON.stringify(record.command),
              JSON.stringify(events),
              JSON.stringify(record.master_effects),
              reason,
              rejectedAt,
              rejectedAt,
            ],
          });
        },
        async addOptimisticEvents(events: readonly LinkedOptimisticStockEvent[]) {
          for (const linked of events) {
            const ids = await linkedIds(linked.command_id);
            ids.add(linked.event_id);
            tasks.push(
              {
                statement:
                  `INSERT INTO pending_stock_command_events(command_id, event_id, event_json)
                   VALUES (?, ?, ?)`,
                values: [
                  linked.command_id,
                  linked.event_id,
                  JSON.stringify(linked.event),
                ],
              },
              {
                statement:
                  `INSERT OR REPLACE INTO events_local(
                     id, server_id, tenant_id, kind, business_id, client_ts,
                     server_ts, rev, payload, self_hash, prev_hash
                   ) VALUES (?, NULL, ?, ?, ?, ?, NULL, NULL, ?, NULL, NULL)`,
                values: [
                  linked.event.client_uuid,
                  linked.event.tenant_id,
                  linked.event.kind,
                  linked.event.business_id,
                  linked.event.client_ts,
                  JSON.stringify(linked.event.payload),
                ],
              },
            );
          }
        },
        async addOptimisticMasterEffects(commandId, effects) {
          const pendingLegacyResult = effects.length === 0
            ? { values: [] as unknown[] }
            : await db.query(
                `SELECT id, payload
                   FROM pending_ops
                  WHERE kind = 'master'
                  ORDER BY id`,
              );
          const pendingLegacyTargets = new Set<string>();
          for (const value of pendingLegacyResult.values ?? []) {
            const pending = value as { id: number; payload: string };
            let payload: { table?: unknown; id?: unknown };
            try {
              payload = JSON.parse(pending.payload) as typeof payload;
            } catch {
              throw new Error(
                `stock_command_pending_legacy_master_corrupt:${pending.id}`,
              );
            }
            if (
              typeof payload.table !== 'string'
              || typeof payload.id !== 'string'
            ) {
              throw new Error(
                `stock_command_pending_legacy_master_corrupt:${pending.id}`,
              );
            }
            pendingLegacyTargets.add(`${payload.table}\u0000${payload.id}`);
          }
          for (const effect of effects) {
            if (
              pendingLegacyTargets.has(`${effect.table}\u0000${effect.id}`)
            ) {
              throw new Error(
                `stock_command_older_legacy_master_overlap:${effect.table}:${effect.id}`,
              );
            }
            const currentResult = await db.query(
              `SELECT tenant_id, version, row, archived, updated_at
                 FROM masters_local
                WHERE table_name = ?
                  AND id = ?`,
              [effect.table, effect.id],
            );
            const current = currentResult.values?.[0] as {
              tenant_id: string | null;
              version: number;
              row: string;
              archived: number;
              updated_at: string;
            } | undefined;
            if (effect.op === 'insert' && current) {
              throw new Error(
                `stock_command_master_insert_exists:${effect.table}:${effect.id}`,
              );
            }
            if (effect.op !== 'insert' && !current) {
              throw new Error(
                `stock_command_master_missing:${effect.table}:${effect.id}`,
              );
            }
            if (
              current
              && Number(current.version) !== effect.base_version
            ) {
              throw new Error(
                `stock_command_master_version_conflict:${effect.table}:${effect.id}`,
              );
            }

            tasks.push({
              statement:
                `INSERT INTO pending_stock_command_master_snapshots(
                   command_id, table_name, id, existed, tenant_id, version,
                   row_json, archived, updated_at
                 ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
              values: [
                commandId,
                effect.table,
                effect.id,
                current ? 1 : 0,
                current?.tenant_id ?? null,
                current == null ? null : Number(current.version),
                current?.row ?? null,
                current == null ? null : Number(current.archived),
                current?.updated_at ?? null,
              ],
            });

            const previousRow = current
              ? JSON.parse(current.row) as Record<string, unknown>
              : {};
            const nextRow: Record<string, unknown> = {
              ...previousRow,
              ...effect.patch,
              id: effect.id,
            };
            if (effect.op === 'archive') {
              nextRow.archived_at =
                (effect.patch.archived_at as string | undefined)
                ?? new Date().toISOString();
            }
            const nextVersion = effect.op === 'insert'
              ? 0
              : effect.base_version + 1;
            nextRow.version = nextVersion;
            const tenantId = (
              nextRow.tenant_id as string | null | undefined
            ) ?? current?.tenant_id ?? null;
            tasks.push({
              statement:
                `INSERT INTO masters_local(
                   table_name, id, tenant_id, version, row, archived, updated_at
                 ) VALUES (?, ?, ?, ?, ?, ?, datetime('now'))
                 ON CONFLICT(table_name,id) DO UPDATE SET
                   tenant_id = excluded.tenant_id,
                   version = excluded.version,
                   row = excluded.row,
                   archived = excluded.archived,
                   updated_at = excluded.updated_at`,
              values: [
                effect.table,
                effect.id,
                tenantId,
                nextVersion,
                JSON.stringify(nextRow),
                nextRow.archived_at ? 1 : 0,
              ],
            });
          }
        },
        async finalizeOptimisticMasterEffects(commandId) {
          if (!writebackPlan) {
            throw new Error('stock_command_ack_writeback_required');
          }
          if (!writebackScheduled) {
            tasks.push(...writebackPlan.tasks);
            writebackScheduled = true;
          }
          const targetsResult = await db.query(
            `SELECT table_name, id
               FROM pending_stock_command_master_snapshots
              WHERE command_id = ?
              ORDER BY table_name, id`,
            [commandId],
          );
          for (const targetValue of targetsResult.values ?? []) {
            const target = targetValue as { table_name: string; id: string };
            const layersResult = await db.query(
              `SELECT snapshot.command_id, snapshot.table_name, snapshot.id,
                      snapshot.existed, snapshot.tenant_id, snapshot.version,
                      snapshot.row_json, snapshot.archived, snapshot.updated_at,
                      command.master_effects_json
                 FROM pending_stock_command_master_snapshots AS snapshot
                 JOIN pending_stock_commands AS command
                   ON command.command_id = snapshot.command_id
                WHERE snapshot.table_name = ?
                  AND snapshot.id = ?
                ORDER BY command.queued_at, command.rowid`,
              [target.table_name, target.id],
            );
            const rows = (layersResult.values ?? []) as PendingMasterLayerRow[];
            const acknowledgedIndex = rows.findIndex(
              (row) => row.command_id === commandId,
            );
            if (acknowledgedIndex < 0) {
              throw new Error(
                `stock_command_master_snapshot_missing:${commandId}:`
                  + `${target.table_name}:${target.id}`,
              );
            }

            // The authoritative server row is part of the same native
            // transaction as the ACK. Starting the replay from the plan (not
            // from masters_local, which may already contain B) prevents a
            // later optimistic layer from being applied twice.
            const base = writebackPlan.authoritativeMasters.get(
              masterTargetKey(target.table_name, target.id),
            );
            if (!base) {
              throw new Error(
                `stock_command_writeback_master_missing:${target.table_name}:${target.id}`,
              );
            }
            const laterLayers = rows
              .slice(acknowledgedIndex + 1)
              .map((row): OptimisticMasterLayer => ({
                command_id: row.command_id,
                effect: effectForLayerTarget(row),
              }));
            const replayed = replayOptimisticMasterLayersForTest(
              base,
              laterLayers,
              new Date().toISOString(),
            );
            for (const layer of replayed.layers) {
              tasks.push(snapshotUpdateTask(
                layer.command_id,
                target.table_name,
                target.id,
                layer.snapshot,
              ));
            }
            tasks.push(...masterStateWriteTasks(
              target.table_name,
              target.id,
              replayed.finalState,
            ));
          }
          tasks.push({
            statement:
              `DELETE FROM pending_stock_command_master_snapshots
                WHERE command_id = ?`,
            values: [commandId],
          });
        },
        async rollbackOptimisticMasterEffects(commandId) {
          const targetsResult = await db.query(
            `SELECT table_name, id
               FROM pending_stock_command_master_snapshots
              WHERE command_id = ?
              ORDER BY table_name, id`,
            [commandId],
          );
          for (const targetValue of targetsResult.values ?? []) {
            const target = targetValue as { table_name: string; id: string };
            const layersResult = await db.query(
              `SELECT snapshot.command_id, snapshot.table_name, snapshot.id,
                      snapshot.existed, snapshot.tenant_id, snapshot.version,
                      snapshot.row_json, snapshot.archived, snapshot.updated_at,
                      command.master_effects_json
                 FROM pending_stock_command_master_snapshots AS snapshot
                 JOIN pending_stock_commands AS command
                   ON command.command_id = snapshot.command_id
                WHERE snapshot.table_name = ?
                  AND snapshot.id = ?
                ORDER BY command.queued_at, command.rowid`,
              [target.table_name, target.id],
            );
            const rows = (layersResult.values ?? []) as PendingMasterLayerRow[];
            const rejectedIndex = rows.findIndex(
              (row) => row.command_id === commandId,
            );
            if (rejectedIndex < 0) {
              throw new Error(
                `stock_command_master_snapshot_missing:${commandId}:`
                  + `${target.table_name}:${target.id}`,
              );
            }

            // The first layer owns the last non-optimistic base still available
            // for this target. Rebuild every surviving layer from that base,
            // excluding the rejected command, and rewrite all snapshots. This
            // prevents B's rollback from restoring A after A was rejected.
            const base = masterStateFromSnapshot(rows[0]);
            const survivingLayers = rows
              .filter((row) => row.command_id !== commandId)
              .map((row): OptimisticMasterLayer => ({
                command_id: row.command_id,
                effect: effectForLayerTarget(row),
              }));
            const replayed = replayOptimisticMasterLayersForTest(
              base,
              survivingLayers,
              new Date().toISOString(),
            );
            for (const layer of replayed.layers) {
              tasks.push(snapshotUpdateTask(
                layer.command_id,
                target.table_name,
                target.id,
                layer.snapshot,
              ));
            }
            tasks.push(...masterStateWriteTasks(
              target.table_name,
              target.id,
              replayed.finalState,
            ));
          }
          tasks.push({
            statement:
              `DELETE FROM pending_stock_command_master_snapshots
                WHERE command_id = ?`,
            values: [commandId],
          });
        },
        async deleteOptimisticEvents(commandId, eventIds) {
          const ids = await linkedIds(commandId);
          let removed = 0;
          for (const eventId of eventIds) {
            if (!ids.has(eventId)) continue;
            removed += 1;
            ids.delete(eventId);
            tasks.push(
              {
                statement:
                  `DELETE FROM events_local
                    WHERE id = ?
                      AND rev IS NULL`,
                values: [eventId],
              },
              {
                statement:
                  `DELETE FROM pending_stock_command_events
                    WHERE command_id = ?
                      AND event_id = ?`,
                values: [commandId, eventId],
              },
            );
          }
          return removed;
        },
      };

      const result = await work(tx);
      if (tasks.length > 0) {
        await db.executeTransaction(tasks, true);
      }
      return result;
    });
}

export const stockCommandSqliteStorage: AtomicStockCommandStorage<StockCommandV1> = {
  transaction: runStorageTransaction,
};

/** A full master pull often repeats unchanged rows. Read the batch once rather
 * than starting a native transaction for each identical row. Optimistic targets
 * always use the normal rebase writer, even if their visible JSON matches. */
export async function changedPulledMasters(masters: readonly PulledMasterChange[]): Promise<PulledMasterChange[]> {
  if (masters.length === 0) return [];
  return serializeStorage(async () => {
    const db = await openLocalDb(null);
    const result = await db.query(
      `SELECT m.table_name, m.id, m.tenant_id, m.version, m.row, m.archived,
         EXISTS(SELECT 1 FROM pending_stock_command_master_snapshots s
           JOIN pending_stock_commands c ON c.command_id = s.command_id
           WHERE s.table_name=m.table_name AND s.id=m.id AND c.status IN ('pending','sending'))
         OR EXISTS(SELECT 1 FROM pending_atomic_rpc_master_snapshots_v1 s
           JOIN pending_atomic_rpcs_v1 r ON r.request_id=s.request_id
           WHERE s.table_name=m.table_name AND s.id=m.id AND r.status IN ('pending','sending')) AS has_overlay
       FROM masters_local m JOIN json_each(?) wanted
         ON m.table_name=json_extract(wanted.value,'$.table') AND m.id=json_extract(wanted.value,'$.id')`,
      [JSON.stringify(masters.map(({ table, id }) => ({ table, id })))],
    );
    const cached = new Map((result.values ?? []).map(row => [`${row.table_name}:${row.id}`, row]));
    return masters.filter(master => {
      const row = cached.get(`${master.table}:${master.id}`);
      if (!row || row.has_overlay || !Number.isInteger(master.version) || master.version < 0
        || row.version !== master.version || row.tenant_id !== (master.row.tenant_id ?? null)
        || row.archived !== (master.row.archived_at ? 1 : 0)) return true;
      try {
        return !deepEqualJson(JSON.parse(row.row as string), { ...master.row, id: master.id, version: master.version });
      } catch { return true; }
    });
  });
}

/**
 * Applies one authoritative sync-pull master without crossing the atomic
 * StockCommand overlay boundary.
 *
 * A pull can run while a command is queued, being acknowledged, or rolling
 * back. The pulled row therefore becomes the new authoritative base and every
 * still-pending optimistic layer is replayed on top of it in the same native
 * SQLite transaction. Older pull pages are ignored so a delayed response
 * cannot downgrade a newer RPC writeback.
 */
export async function applyPulledMasterToStockCommandCache(
  master: PulledMasterChange,
  options: {
    excludeAtomicRequestId?: string;
    excludeStockCommandIds?: ReadonlySet<string>;
    /**
     * A semantically validated atomic RPC receipt is authoritative even when
     * its server version is lower than the optimistic local version (for
     * example after close-day absorbed several legacy draft bumps). The
     * bypass is accepted only when the active exact-target envelope and digest
     * still match in SQLite.
     */
    trustedAtomicReceipt?: {
      requestId: string;
      digest: string;
    };
  } = {},
): Promise<void> {
  await serializeStorage(async () => {
    if (!Number.isInteger(master.version) || master.version < 0) {
      throw new Error('pulled_master_version_invalid');
    }
    const db = await openLocalDb(null);
    let trustedAtomicReceipt = false;
    if (options.trustedAtomicReceipt) {
      if (
        options.excludeAtomicRequestId
        !== options.trustedAtomicReceipt.requestId
      ) {
        throw new Error('atomic_receipt_exclusion_mismatch');
      }
      const trustedResult = await db.query(
        `SELECT rpc.request_id
           FROM pending_atomic_rpcs_v1 AS rpc
           JOIN pending_atomic_rpc_master_snapshots_v1 AS snapshot
             ON snapshot.request_id = rpc.request_id
          WHERE rpc.request_id = ?
            AND rpc.digest = ?
            AND rpc.status IN ('pending','sending')
            AND snapshot.table_name = ?
            AND snapshot.id = ?
          LIMIT 1`,
        [
          options.trustedAtomicReceipt.requestId,
          options.trustedAtomicReceipt.digest,
          master.table,
          master.id,
        ],
      );
      if ((trustedResult.values?.length ?? 0) !== 1) {
        throw new Error('atomic_receipt_identity_mismatch');
      }
      trustedAtomicReceipt = true;
    }
    const stockLayersResult = await db.query(
      `SELECT snapshot.command_id, snapshot.table_name, snapshot.id,
              snapshot.existed, snapshot.tenant_id, snapshot.version,
              snapshot.row_json, snapshot.archived, snapshot.updated_at,
              command.master_effects_json, command.queued_at
         FROM pending_stock_command_master_snapshots AS snapshot
         JOIN pending_stock_commands AS command
           ON command.command_id = snapshot.command_id
        WHERE snapshot.table_name = ?
          AND snapshot.id = ?
          AND command.status IN ('pending','sending')
        ORDER BY command.queued_at, command.rowid`,
      [master.table, master.id],
    );
    const stockRows = (
      stockLayersResult.values ?? []
    ) as PendingMasterLayerRow[];
    const atomicLayersResult = await db.query(
      `SELECT snapshot.request_id, snapshot.table_name, snapshot.id,
              snapshot.existed, snapshot.tenant_id, snapshot.version,
              snapshot.row_json, snapshot.archived, snapshot.updated_at,
              snapshot.effect_json, rpc.queued_at
         FROM pending_atomic_rpc_master_snapshots_v1 AS snapshot
         JOIN pending_atomic_rpcs_v1 AS rpc
           ON rpc.request_id = snapshot.request_id
        WHERE snapshot.table_name = ?
          AND snapshot.id = ?
          AND rpc.status IN ('pending','sending')
        ORDER BY rpc.queued_at, rpc.rowid`,
      [master.table, master.id],
    );
    const atomicRows = (
      atomicLayersResult.values ?? []
    ) as PendingAtomicMasterLayerRow[];
    const layers: UnifiedOptimisticMasterLayer[] = [
      ...atomicRows.map((row): UnifiedOptimisticMasterLayer => ({
        ownerKind: 'atomic',
        ownerId: row.request_id,
        queuedAt: row.queued_at,
        snapshot: masterStateFromSnapshot(row),
        effect: JSON.parse(row.effect_json) as StockCommandMasterEffectV1,
      })),
      ...stockRows.map((row): UnifiedOptimisticMasterLayer => ({
        ownerKind: 'stock',
        ownerId: row.command_id,
        queuedAt: row.queued_at ?? '',
        snapshot: masterStateFromSnapshot(row),
        effect: effectForLayerTarget(row),
      })),
    ].sort((left, right) => (
      left.queuedAt.localeCompare(right.queuedAt)
      || (
        left.ownerKind === right.ownerKind
          ? left.ownerId.localeCompare(right.ownerId)
          : left.ownerKind === 'atomic' ? -1 : 1
      )
    ));
    const currentResult = await db.query(
      `SELECT tenant_id, version, row, archived, updated_at
         FROM masters_local
        WHERE table_name = ?
          AND id = ?`,
      [master.table, master.id],
    );
    const current = currentResult.values?.[0] as {
      tenant_id: string | null;
      version: number;
      row: string;
      archived: number;
      updated_at: string;
    } | undefined;
    const authoritativeBase = layers.length > 0
      ? layers[0].snapshot
      : current
        ? {
            existed: true,
            tenant_id: current.tenant_id,
            version: Number(current.version),
            row_json: current.row,
            archived: Number(current.archived),
            updated_at: current.updated_at,
          } satisfies OptimisticMasterState
        : null;
    const pulledAt = typeof master.row.updated_at === 'string'
      ? master.row.updated_at
      : new Date().toISOString();
    const pulledRow: Record<string, unknown> = {
      ...master.row,
      id: master.id,
      version: master.version,
    };
    const staleAgainstRebasedAuthoritativeBase =
      authoritativeBase?.version != null
      && master.version < authoritativeBase.version;
    if (staleAgainstRebasedAuthoritativeBase && !trustedAtomicReceipt) return;
    // A delayed but identity-valid receipt must finalize its envelope without
    // downgrading a newer authoritative pull. In that case remove/replay the
    // acknowledged layer from the newer snapshot instead of applying the old
    // receipt row. The absorbed "unknown base" sentinel has version=null and
    // therefore still accepts the first lower real server receipt.
    const pulledState: OptimisticMasterState =
      staleAgainstRebasedAuthoritativeBase
        ? authoritativeBase
        : {
            existed: true,
            tenant_id:
              (pulledRow.tenant_id as string | null | undefined) ?? null,
            version: master.version,
            row_json: JSON.stringify(pulledRow),
            archived: pulledRow.archived_at ? 1 : 0,
            updated_at: pulledAt,
          };
    const tasks: SqlTask[] = [];
    const survivingLayers = layers.filter((layer) => (
      !(
        layer.ownerKind === 'atomic'
        && layer.ownerId === options.excludeAtomicRequestId
      )
      && !(
        layer.ownerKind === 'stock'
        && options.excludeStockCommandIds?.has(layer.ownerId)
      )
    ));
    if (survivingLayers.length === 0) {
      tasks.push(...masterStateWriteTasks(master.table, master.id, pulledState));
    } else {
      let state = pulledState;
      const replayedAt = new Date().toISOString();
      for (const layer of survivingLayers) {
        if (layer.ownerKind === 'atomic') {
          tasks.push(atomicSnapshotUpdateTask(
            layer.ownerId,
            master.table,
            master.id,
            state,
          ));
        } else {
          tasks.push(snapshotUpdateTask(
            layer.ownerId,
            master.table,
            master.id,
            state,
          ));
        }
        state = applyMasterEffectForReplay(state, layer.effect, replayedAt);
      }
      tasks.push(...masterStateWriteTasks(
        master.table,
        master.id,
        state,
      ));
    }
    await db.executeTransaction(tasks, true);
  });
}

export async function acknowledgeStockCommandWriteback(
  handle: EnqueuedStockCommandHandle,
  result: Record<string, unknown>,
): Promise<StockCommandAckResult> {
  const plan = stockCommandWritebackPlan(result);
  return acknowledgeStockCommand(
    {
      transaction: (work) => runStorageTransaction(work, plan),
    },
    handle,
  );
}

export async function applyStockCommandWritebackLocally(
  result: Record<string, unknown>,
): Promise<void> {
  const plan = stockCommandWritebackPlan(result);
  await serializeStorage(async () => {
    if (plan.tasks.length === 0) return;
    const db = await openLocalDb(null);
    await db.executeTransaction(plan.tasks, true);
  });
}

/**
 * Legacy masters are still pushed through `pending_ops`, but their outbox row
 * and optimistic cache update must share the stock-command serialization
 * boundary. If an earlier StockCommand owns the same master, fail before
 * enqueueing: legacy push is drained first and would otherwise reorder OCC.
 */
export interface RegisterLocalCommit {
  accountId: string;
  tenantId: string;
  businessId: string | null;
  assertScope: () => void;
}

export async function enqueueLegacyMasterOp(op: MasterOp, register?: RegisterLocalCommit): Promise<void> {
  await serializeStorage(async () => {
    const db = await openLocalDb(null);
    if (register) {
      register.assertScope();
      if (!isOfflineRegisterTable(op.table) || op.op !== 'insert'
        || op.patch.tenant_id !== register.tenantId || op.patch.business_id !== register.businessId) {
        throw new Error('Ambito del registro non valido');
      }
    }
    const atomicOverlap = await db.query(
      `SELECT snapshot.request_id
         FROM pending_atomic_rpc_master_snapshots_v1 AS snapshot
         JOIN pending_atomic_rpcs_v1 AS rpc
           ON rpc.request_id = snapshot.request_id
        WHERE snapshot.table_name = ?
          AND snapshot.id = ?
        LIMIT 1`,
      [op.table, op.id],
    );
    if ((atomicOverlap.values?.length ?? 0) > 0) {
      throw new Error(`atomic_rpc_legacy_master_overlap:${op.table}:${op.id}`);
    }
    const overlapping = await db.query(
      `SELECT command_id
         FROM pending_stock_command_master_snapshots
        WHERE table_name = ?
          AND id = ?
        LIMIT 1`,
      [op.table, op.id],
    );
    if ((overlapping.values?.length ?? 0) > 0) {
      throw new Error(`stock_command_legacy_master_overlap:${op.table}:${op.id}`);
    }

    let mergedRow: Record<string, unknown> = { ...op.patch, id: op.id };
    const existing = await db.query(
      `SELECT row
         FROM masters_local
        WHERE table_name = ?
          AND id = ?`,
      [op.table, op.id],
    );
    const existingRowJson = (
      existing.values?.[0] as { row?: string } | undefined
    )?.row;
    if (existingRowJson) {
      const existingRow = JSON.parse(existingRowJson) as Record<string, unknown>;
      if (register && isOfflineRegisterTable(op.table)) {
        const selected = Object.fromEntries(Object.keys(op.patch).map((key) => [key, existingRow[key]]));
        if ((existingRow[REGISTER_OWNER_FIELD] && existingRow[REGISTER_OWNER_FIELD] !== register.accountId)
          || !deepEqualJson(validateOfflineRegisterPatch(op.table, selected), op.patch)) {
          throw new Error('Questo salvataggio esiste già con dati diversi');
        }
        const rejected = await db.query(`SELECT reason FROM pending_op_reconciliations_v1
          WHERE kind = 'master' AND status = 'reconciliation_required'
            AND json_valid(payload_json) AND json_extract(payload_json, '$.table') = ?
            AND json_extract(payload_json, '$.id') = ? LIMIT 1`, [op.table, op.id]);
        if (rejected.values?.length) throw new Error('Questo registro è stato rifiutato: consulta lo stato nella lista');
        register.assertScope();
        await flushLocalDb({ required: true });
        register.assertScope();
        return;
      }
      mergedRow = { ...existingRow, ...op.patch, id: op.id };
    }
    if (register) mergedRow[REGISTER_OWNER_FIELD] = register.accountId;
    const optimisticVersion = op.op === 'insert' ? 0 : op.base_version + 1;
    mergedRow.version = optimisticVersion;
    const tenantId = (
      mergedRow.tenant_id as string | null | undefined
    ) ?? (op.patch.tenant_id as string | null | undefined) ?? null;
    let blockingReopenRequestId: string | null = null;
    if (op.table === 'productions') {
      const businessId = (
        mergedRow.business_id as string | null | undefined
      ) ?? null;
      const sheetDate = serviceDateInRome(mergedRow.produced_at);
      if (tenantId && businessId && sheetDate) {
        const reopenResult = await db.query(
          `SELECT request_id,status,request_json
             FROM pending_atomic_rpcs_v1
            WHERE kind = 'production_day_transition_v1'
              AND tenant_id = ?
              AND business_id = ?
              AND status IN ('pending','sending','conflict')
            ORDER BY queued_at,request_id`,
          [tenantId, businessId],
        );
        for (const value of reopenResult.values ?? []) {
          const candidate = value as {
            request_id: string;
            status: string;
            request_json: string;
          };
          let request: {
            transition?: unknown;
            sheet_date?: unknown;
          };
          try {
            request = JSON.parse(candidate.request_json) as typeof request;
          } catch {
            throw new Error('production_day_reopen_dependency_corrupt');
          }
          if (
            request.transition !== 'reopen'
            || request.sheet_date !== sheetDate
          ) {
            continue;
          }
          if (candidate.status === 'conflict') {
            throw new Error(
              'production_day_reopen_conflict_requires_resolution',
            );
          }
          blockingReopenRequestId = candidate.request_id;
          break;
        }
      }
    }
    let pendingPayload: Record<string, unknown> = blockingReopenRequestId
      ? {
          ...op,
          __trackeat_atomic_prerequisite_request_id:
            blockingReopenRequestId,
        }
      : op;
    if (register) pendingPayload = { ...pendingPayload, [REGISTER_OWNER_FIELD]: register.accountId };
    const tasks: SqlTask[] = [{
      statement: `INSERT INTO pending_ops(kind, payload) VALUES ('master', ?)`,
      values: [JSON.stringify(pendingPayload)],
    }];
    if (!blockingReopenRequestId) {
      tasks.push({
        statement:
          `INSERT INTO masters_local(
             table_name,id,tenant_id,version,row,archived,updated_at
           ) VALUES(?,?,?,?,?,?,datetime('now'))
           ON CONFLICT(table_name,id) DO UPDATE SET
             row = excluded.row,
             version = excluded.version,
             archived = excluded.archived,
             updated_at = excluded.updated_at`,
        values: [
          op.table,
          op.id,
          tenantId,
          optimisticVersion,
          JSON.stringify(mergedRow),
          op.op === 'archive' ? 1 : 0,
        ],
      });
    }
    if (register) {
      tasks.push({
        statement: `DELETE FROM work_draft_cache_v1 WHERE account_id = ? AND tenant_id = ?
          AND business_id = ? AND resource_kind = 'haccp_register' AND resource_key = ?
          AND json_extract(payload, '$.id') = ?`,
        values: [register.accountId, register.tenantId, register.businessId ?? '', op.table, op.id],
      });
      register.assertScope();
    }
    await db.executeTransaction(tasks, true);
    if (register) {
      await flushLocalDb({ required: true });
      register.assertScope();
    }
  });
}

export async function listPendingStockCommandBatches(
  limit = 20,
  tenantId?: string,
  excludedCommandIds: ReadonlySet<string> = new Set(),
): Promise<PendingStockCommandBatch[]> {
  const db = await openLocalDb(null);
  const tenantFilter = tenantId ? 'AND tenant_id = ?' : '';
  const excludedIds = [...excludedCommandIds];
  const exclusionFilter = excludedIds.length > 0
    ? `AND command_id NOT IN (${excludedIds.map(() => '?').join(',')})`
    : '';
  const result = await db.query(
    `SELECT command_id, tenant_id, digest, command_json,
            master_effects_json, status, attempts,
            optimistic_event_ids, last_error, conflict_json, queued_at, updated_at
       FROM pending_stock_commands
      WHERE status IN ('pending','sending')
        ${tenantFilter}
        ${exclusionFilter}
        AND (last_error IS NULL OR last_error NOT LIKE 'client_prepare_failed:%'
          OR julianday(updated_at) <= julianday(?))
        AND NOT EXISTS (
          SELECT 1
            FROM atomic_rpc_dependents_v1 dependency
           WHERE dependency.dependent_kind = 'stock_command'
             AND dependency.dependent_id = pending_stock_commands.command_id
             AND dependency.status IN ('pending','quarantined')
        )
      ORDER BY attempts ASC, queued_at ASC
      LIMIT ?`,
    // A bad first page must not starve unrelated later commands. Keep the
    // operation durable and retry after five minutes (including after upgrade).
    tenantId
      ? [tenantId, ...excludedIds, new Date(Date.now() - 300_000).toISOString(), limit]
      : [...excludedIds, new Date(Date.now() - 300_000).toISOString(), limit],
  );
  const records: StockCommandOutboxRecord<StockCommandV1>[] = [];
  for (const value of result.values ?? []) {
    const row = value as PendingCommandRow;
    try {
      records.push(parseCommandRow(row));
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await db.run(
        `UPDATE pending_stock_commands
            SET status = 'pending', attempts = attempts + 1,
                last_error = ?, updated_at = ?
          WHERE command_id = ?`,
        [
          `client_prepare_failed:${reason}`,
          new Date().toISOString(),
          row.command_id,
        ],
      );
    }
  }

  const batches: PendingStockCommandBatch[] = [];
  for (const record of records) {
    try {
      const eventRows = await db.query(
        `SELECT command_id, event_id, event_json
           FROM pending_stock_command_events
          WHERE command_id = ?`,
        [record.command_id],
      );
      const byId = new Map(
        (eventRows.values ?? []).map((value) => {
          const row = value as LinkedEventRow;
          return [row.event_id, JSON.parse(row.event_json) as SyncEventInput] as const;
        }),
      );
      const events = record.optimistic_event_ids
        .map((eventId) => byId.get(eventId))
        .filter((event): event is SyncEventInput => event !== undefined);
      if (events.length !== record.optimistic_event_ids.length) {
        throw new Error(
          `stock_command_local_event_set_incomplete:${record.command_id}:`
            + `${events.length}/${record.optimistic_event_ids.length}`,
        );
      }
      batches.push({
        record,
        handle: {
          command_id: record.command_id,
          digest: record.digest,
          write_path: 'stock_command_only',
        },
        events,
      });
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await db.run(
        `UPDATE pending_stock_commands
            SET status = 'pending', attempts = attempts + 1,
                last_error = ?, updated_at = ?
          WHERE command_id = ?`,
        [
          `client_prepare_failed:${reason}`,
          new Date().toISOString(),
          record.command_id,
        ],
      );
    }
  }
  return batches;
}

export async function getPendingStockCommandBatch(
  commandId: string,
): Promise<PendingStockCommandBatch | null> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT command_id, tenant_id, digest, command_json,
            master_effects_json, status, attempts,
            optimistic_event_ids, last_error, conflict_json, queued_at, updated_at
       FROM pending_stock_commands
      WHERE command_id = ?
        AND status IN ('pending','sending')
        AND NOT EXISTS (
          SELECT 1
            FROM atomic_rpc_dependents_v1 dependency
           WHERE dependency.dependent_kind = 'stock_command'
             AND dependency.dependent_id = pending_stock_commands.command_id
             AND dependency.status IN ('pending','quarantined')
        )
      LIMIT 1`,
    [commandId],
  );
  const raw = result.values?.[0] as PendingCommandRow | undefined;
  if (!raw) return null;
  const record = parseCommandRow(raw);
  const eventRows = await db.query(
    `SELECT command_id, event_id, event_json
       FROM pending_stock_command_events
      WHERE command_id = ?`,
    [commandId],
  );
  const byId = new Map(
    (eventRows.values ?? []).map((value) => {
      const row = value as LinkedEventRow;
      return [row.event_id, JSON.parse(row.event_json) as SyncEventInput] as const;
    }),
  );
  const events = record.optimistic_event_ids
    .map((eventId) => byId.get(eventId))
    .filter((event): event is SyncEventInput => event !== undefined);
  if (events.length !== record.optimistic_event_ids.length) {
    throw new Error(
      `stock_command_local_event_set_incomplete:${record.command_id}:`
        + `${events.length}/${record.optimistic_event_ids.length}`,
    );
  }
  return {
    record,
    handle: {
      command_id: record.command_id,
      digest: record.digest,
      write_path: 'stock_command_only',
    },
    events,
  };
}

export async function countPendingStockCommands(
  tenantId?: string,
  includeConflicts = false,
): Promise<number> {
  const db = await openLocalDb(null);
  const statuses = includeConflicts
    ? `('pending','sending','conflict')`
    : `('pending','sending')`;
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM pending_stock_commands
      WHERE status IN ${statuses}
        ${tenantId ? 'AND tenant_id = ?' : ''}`,
    tenantId ? [tenantId] : [],
  );
  return Number((result.values?.[0] as { count?: number } | undefined)?.count ?? 0);
}

export interface StockCommandReconciliation {
  command_id: string;
  digest: string;
  tenant_id: string;
  command: StockCommandV1;
  events: SyncEventInput[];
  master_effects: StockCommandMasterEffectV1[];
  reason: string;
  status: 'reconciliation_required' | 'resolved';
  resolution: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export async function listStockCommandReconciliations(input: {
  tenantId: string;
  includeResolved?: boolean;
}): Promise<StockCommandReconciliation[]> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT command_id,digest,tenant_id,command_json,events_json,
            master_effects_json,reason,status,resolution,created_at,
            updated_at,resolved_at
       FROM stock_command_reconciliations_v1
      WHERE tenant_id = ?
        ${input.includeResolved ? '' : "AND status = 'reconciliation_required'"}
      ORDER BY created_at ASC`,
    [input.tenantId],
  );
  return (result.values ?? []).map((value) => {
    const row = value as {
      command_id: string;
      digest: string;
      tenant_id: string;
      command_json: string;
      events_json: string;
      master_effects_json: string;
      reason: string;
      status: StockCommandReconciliation['status'];
      resolution: string | null;
      created_at: string;
      updated_at: string;
      resolved_at: string | null;
    };
    return {
      command_id: row.command_id,
      digest: row.digest,
      tenant_id: row.tenant_id,
      command: JSON.parse(row.command_json) as StockCommandV1,
      events: JSON.parse(row.events_json) as SyncEventInput[],
      master_effects: JSON.parse(row.master_effects_json) as StockCommandMasterEffectV1[],
      reason: row.reason,
      status: row.status,
      resolution: row.resolution,
      created_at: row.created_at,
      updated_at: row.updated_at,
      resolved_at: row.resolved_at,
    };
  });
}

export async function countStockCommandReconciliations(
  tenantId?: string,
): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM stock_command_reconciliations_v1
      WHERE status = 'reconciliation_required'
        ${tenantId ? 'AND tenant_id = ?' : ''}`,
    tenantId ? [tenantId] : [],
  );
  return Number((result.values?.[0] as { count?: number } | undefined)?.count ?? 0);
}

export async function resolveStockCommandReconciliation(input: {
  commandId: string;
  tenantId: string;
  resolution?: string;
}): Promise<void> {
  const db = await openLocalDb(null);
  const resolvedAt = new Date().toISOString();
  await db.run(
    `UPDATE stock_command_reconciliations_v1
        SET status = 'resolved',
            resolution = ?,
            updated_at = ?,
            resolved_at = ?
      WHERE command_id = ?
        AND tenant_id = ?
        AND status = 'reconciliation_required'`,
    [
      input.resolution ?? 'physical_stock_verified',
      resolvedAt,
      resolvedAt,
      input.commandId,
      input.tenantId,
    ],
  );
}

export interface LegacyPermanentStockCommandRejection {
  command_id: string;
  reason: string | null;
  finalized_at: string;
}

/**
 * PWA builds before T-560 deleted the rejected envelope before a dead-letter
 * snapshot existed. Keep those tombstones visible as legacy incidents; their
 * product/lot cannot be reconstructed automatically or safely.
 */
export async function listUnarchivedPermanentStockCommandRejections(): Promise<
  LegacyPermanentStockCommandRejection[]
> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT finalization.command_id,finalization.reason,finalization.finalized_at
       FROM stock_command_finalizations AS finalization
      WHERE finalization.outcome = 'permanently_rejected'
        AND NOT EXISTS (
          SELECT 1
            FROM stock_command_reconciliations_v1 AS reconciliation
           WHERE reconciliation.command_id = finalization.command_id
        )
        AND NOT EXISTS (
          SELECT 1
            FROM stock_command_legacy_acknowledgements_v1 AS acknowledgement
           WHERE acknowledgement.command_id = finalization.command_id
        )
      ORDER BY finalization.finalized_at ASC`,
  );
  return (result.values ?? []) as LegacyPermanentStockCommandRejection[];
}

export async function countUnarchivedPermanentStockCommandRejections(): Promise<number> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT COUNT(*) AS count
       FROM stock_command_finalizations AS finalization
      WHERE finalization.outcome = 'permanently_rejected'
        AND NOT EXISTS (
          SELECT 1
            FROM stock_command_reconciliations_v1 AS reconciliation
           WHERE reconciliation.command_id = finalization.command_id
        )
        AND NOT EXISTS (
          SELECT 1
            FROM stock_command_legacy_acknowledgements_v1 AS acknowledgement
           WHERE acknowledgement.command_id = finalization.command_id
        )`,
  );
  return Number((result.values?.[0] as { count?: number } | undefined)?.count ?? 0);
}

export async function acknowledgeLegacyPermanentStockCommandRejection(input: {
  commandId: string;
  reason: string | null;
  resolution?: string;
}): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO stock_command_legacy_acknowledgements_v1(
       command_id,reason,resolution,resolved_at
     ) VALUES(?,?,?,?)
     ON CONFLICT(command_id) DO NOTHING`,
    [
      input.commandId,
      input.reason,
      input.resolution ?? 'physical_stock_verified_without_legacy_snapshot',
      new Date().toISOString(),
    ],
  );
}

/**
 * Permanent command failures are durable user-facing incidents, not transient
 * transport noise. Keep returning the latest tombstone across app restarts so
 * a rejected correction/stock movement cannot disappear behind a later
 * successful sync tick.
 */
export async function latestPermanentStockCommandRejection(): Promise<
  StockCommandFinalization | null
> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT command_id, digest, outcome, reason, finalized_at
       FROM stock_command_finalizations
      WHERE outcome = 'permanently_rejected'
      ORDER BY finalized_at DESC
      LIMIT 1`,
  );
  const row = result.values?.[0] as FinalizationRow | undefined;
  return row ? parseFinalizationRow(row) : null;
}
